import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { NotificationService } from '../../../../../../../common/services/notification/notification.service';
import { UliItemAttachmentsService } from './uli-item-attachments.service';
import {
  CombinedAttachmentsModel,
  STItemAttachmentModel,
  UliAttachmentsModel,
  UliItemAttachmentModel,
} from '../../../models/allocation-view-uli-item.model';
import {
  UliItemAttachmentsDownloadPdf,
  UliItemAttachmentsGetAttachments,
  UliItemAttachmentsSetInitialState,
} from './uli-item-attachments.actions';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Error, CustomHttpErrorResponse } from '../../../../../../../common/constants/error';
import { downloadBlob } from '../../../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { BookingHistoryConfirmationResponseModel } from '../../../models';

interface AllocationViewUliItemStateModel {
  attachments: CombinedAttachmentsModel;
  loaded: boolean;
}

const initialState: AllocationViewUliItemStateModel = {
  attachments: null,
  loaded: false,
};

@State<AllocationViewUliItemStateModel>({
  name: 'allocationViewUliItemAttachments',
  defaults: initialState,
})
@Injectable()
export class UliItemAttachmentsState {
  @Selector()
  static getState(state: AllocationViewUliItemStateModel): AllocationViewUliItemStateModel {
    return state;
  }

  @Selector()
  static getAttachments(state: AllocationViewUliItemStateModel): CombinedAttachmentsModel {
    return state.attachments;
  }

  @Selector()
  static getLoaded(state: AllocationViewUliItemStateModel): boolean {
    return state.loaded;
  }

  constructor(
    private allocationViewUliItemAttachmentsService: UliItemAttachmentsService,
    private notificationService: NotificationService,
  ) {}

  @Action(UliItemAttachmentsGetAttachments)
  getAttachments(
    ctx: StateContext<AllocationViewUliItemStateModel>,
    { payload }: UliItemAttachmentsGetAttachments,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.allocationViewUliItemAttachmentsService.getUliAttachments(payload).pipe(
      map((combinedAttachments: CombinedAttachmentsModel) => {
        const uliAttachments: UliItemAttachmentModel[] = combinedAttachments.uliAttachmentViews.sort(
          (a: UliItemAttachmentModel, b: UliItemAttachmentModel) => a.creationDate.timestamp - b.creationDate.timestamp,
        );

        const truckAttachments: STItemAttachmentModel[] = combinedAttachments.stTourExecutionAttachmentViews.map(
          (stAttachment: STItemAttachmentModel) => ({
            ...stAttachment,
            attachments: [...stAttachment.attachments].sort(
              (a: UliItemAttachmentModel, b: UliItemAttachmentModel) =>
                a.creationDate.timestamp - b.creationDate.timestamp,
            ),
          }),
        );

        const sortedAttachmentsModel: CombinedAttachmentsModel = {
          uliAttachmentViews: uliAttachments,
          stTourExecutionAttachmentViews: truckAttachments,
        };

        ctx.patchState({ attachments: sortedAttachmentsModel });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliItemAttachmentsDownloadPdf)
  downloadPDF(ctx: StateContext<AllocationViewUliItemStateModel>, { payload }: UliItemAttachmentsDownloadPdf): void {
    this.getPDFInBlob(payload).subscribe((response: BookingHistoryConfirmationResponseModel) =>
      downloadBlob(response.data, response.fileName),
    );
  }

  @Action(UliItemAttachmentsSetInitialState)
  setInitialState(ctx: StateContext<AllocationViewUliItemStateModel>): void {
    ctx.setState(initialState);
  }

  private getPDFInBlob(attachment: UliAttachmentsModel): Observable<BookingHistoryConfirmationResponseModel> {
    return this.allocationViewUliItemAttachmentsService.getPDF(attachment.attachmentId).pipe(
      map((base64Data: string) => {
        const fileName = attachment.filename;

        try {
          const cleanedBase64 = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;
          const binaryString = atob(decodeURIComponent(cleanedBase64.trim()));
          const byteArray = Uint8Array.from(binaryString, (c: string) => c.charCodeAt(0));
          const blob = new Blob([byteArray], { type: 'application/pdf' });

          return {
            data: blob,
            fileName,
          };
        } catch (error) {
          return {
            data: new Blob([], { type: 'application/pdf' }),
            fileName,
          };
        }
      }),
    );
  }
}
